import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import API_BASE_URL from '../config/apiConfig';
import roomBackground from '../assets/caydan.png';
import bedroomImg from '../assets/bedroom.png';
import livingImg from '../assets/living.png';
import kitchenImg from '../assets/kitchen.png';
import bathroomImg from '../assets/bathroom.png';
import aromaImg from '../assets/aroma.png';
import humiImg from '../assets/humi.png';
import speakerImg from '../assets/speaker.png';
import lampImg from '../assets/smartlamp.png';
// Smart Socket sẽ dùng SVG fallback (không dùng file hình)
const socketImg = null;

const RoomDetail = ({ isVisible, onBack, onLogout, roomName = 'Living Room', onDeviceClick, onNavClick }) => {
  const [deviceStates, setDeviceStates] = useState({
    1: true,  // Smart Lamp - on
    2: false, // Speaker - off
    3: false, // Aroma Diffuser - off
    4: false,  // Humidifier - off
    5: false,  // Smart Socket - off (chỉ có ở Living Room)
  });
  const [socketPower, setSocketPower] = useState(0.0);
  const [socketLoading, setSocketLoading] = useState(false);

  // Danh sách devices - thêm Smart Socket cho Living Room
  const getDevicesForRoom = () => {
    const baseDevices = [
      { id: 1, name: 'Smart Lamp', room: 'Living Room', icon: '💡', color: '#93c5fd', active: true },
      { id: 2, name: 'Speaker', room: 'Living Room', icon: '🔊', color: '#ffffff', active: false },
      { id: 3, name: 'Aroma Diffuser', room: 'Living Room', icon: '🌬️', color: '#ffffff', active: false },
      { id: 4, name: 'Humidifier', room: 'Living Room', icon: '💧', color: '#ffffff', active: false },
    ];
    
    // Thêm Smart Socket nếu là Living Room (so sánh không phân biệt hoa thường)
    const roomNameLower = (roomName || '').toLowerCase();
    if (roomNameLower === 'living room' || roomNameLower === 'livingroom') {
      return [
        ...baseDevices,
        { id: 5, name: 'Smart Socket', room: 'Living Room', icon: '🔌', color: '#ffffff', active: false },
      ];
    }
    
    return baseDevices;
  };

  const devices = getDevicesForRoom();

  // Tìm Smart Socket device
  const smartSocketDevice = devices.find(d => d.name === 'Smart Socket');

  // Fetch power và status cho Smart Socket
  useEffect(() => {
    const roomNameLower = (roomName || '').toLowerCase();
    if (!isVisible || (roomNameLower !== 'living room' && roomNameLower !== 'livingroom') || !smartSocketDevice) return;

    const fetchPower = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/devices/power`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const powerValue = data.power || 0.0;
          setSocketPower(powerValue);
        }
      } catch (error) {
        console.error('Error fetching power:', error);
      }
    };

    const fetchStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/devices/status`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const newStatus = data.isOn || false;
          const socketDeviceId = smartSocketDevice.id;
          // Chỉ cập nhật nếu khác với trạng thái hiện tại và không đang loading (tránh loop)
          if (newStatus !== deviceStates[socketDeviceId] && !socketLoading) {
            setDeviceStates(prev => ({
              ...prev,
              [socketDeviceId]: newStatus
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching status:', error);
      }
    };

    // Fetch ngay lần đầu
    fetchPower();
    fetchStatus();

    // Cập nhật mỗi 1 giây
    const powerInterval = setInterval(fetchPower, 1000);
    const statusInterval = setInterval(fetchStatus, 1000);

    return () => {
      clearInterval(powerInterval);
      clearInterval(statusInterval);
    };
  }, [isVisible, roomName, smartSocketDevice, deviceStates, socketLoading]);

  if (!isVisible) return null;

  const getDeviceImage = (name) => {
    if (name.includes('Lamp')) return lampImg;
    if (name.includes('Speaker')) return speakerImg;
    if (name.includes('Aroma')) return aromaImg;
    if (name.includes('Humidifier')) return humiImg;
    if (name.includes('Socket')) return socketImg; // Thêm hình smart socket
    return null;
  };

  // Background luôn dùng hình caydan.png
  // Các hình phòng cụ thể (bedroom, living, kitchen, bathroom) có thể dùng ở chỗ khác

  // Toggle device - với logic đặc biệt cho Smart Socket
  const toggleDevice = async (deviceId) => {
    const device = devices.find(d => d.id === deviceId);
    const isSmartSocket = device && device.name === 'Smart Socket';
    
    if (isSmartSocket) {
      // Logic đặc biệt cho Smart Socket - gọi API
      const newValue = !deviceStates[deviceId];
      setSocketLoading(true);
      
      try {
        const cmd = newValue ? "ON" : "OFF";
        console.log('Toggling socket to:', cmd);
        
        const response = await fetch(`${API_BASE_URL}/api/devices/led?state=${cmd}`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.text();
          console.log('Toggle response:', result);
          setDeviceStates(prev => ({
            ...prev,
            [deviceId]: newValue
          }));
        } else {
          const errorText = await response.text();
          throw new Error(`Server returned ${response.status}: ${errorText}`);
        }
      } catch (error) {
        console.error('Error toggling socket:', error);
        alert(`Lỗi khi ${newValue ? 'bật' : 'tắt'} socket: ${error.message}`);
        // Không thay đổi state nếu lỗi
      } finally {
        setSocketLoading(false);
      }
    } else {
      // Logic bình thường cho các device khác
      setDeviceStates(prev => ({
        ...prev,
        [deviceId]: !prev[deviceId]
      }));
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar activeId="rooms" onLogout={onLogout} onNavClick={onNavClick} />

      <div className="room-detail-content">
        {/* Background Image with Blur - Luôn dùng hình caydan.png */}
        <div className="room-background" style={{ backgroundImage: `url(${roomBackground})` }}></div>

        {/* Header */}
        <div className="room-detail-header">
          <div className="room-header-left">
            <button className="back-btn-header" onClick={onBack}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>
            <div className="room-breadcrumb">
              <span className="breadcrumb-item">My Home</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
              <span className="breadcrumb-current">{roomName}</span>
            </div>
          </div>
          <div className="room-header-right">
            <button className="room-header-icon-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </button>
            <button className="room-header-icon-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="1" />
                <circle cx="12" cy="5" r="1" />
                <circle cx="12" cy="19" r="1" />
              </svg>
            </button>
          </div>
        </div>

        {/* Device Control Cards - Horizontal Scroll */}
        <div className="room-device-cards-horizontal">
          <div className="room-device-cards-scroll">
            {devices.map((device) => {
              const isActive = deviceStates[device.id];
              return (
              <div 
                key={device.id} 
                className={`room-device-card ${isActive ? 'active' : ''}`}
                onClick={() => onDeviceClick && onDeviceClick(device.name, device.room)}
              >
                <div className="room-device-icon-wrapper">
                  {device.name === 'Smart Socket' ? (
                    socketImg ? (
                      <img src={socketImg} alt={device.name} style={{width: '100%', height: '100%', objectFit: 'contain', padding: '8px'}} />
                    ) : (
                      <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke={deviceStates[device.id] ? "#ff8c00" : "#9ca3af"} strokeWidth="1.5" style={{padding: '8px', transition: 'stroke 0.3s'}}>
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                      </svg>
                    )
                  ) : getDeviceImage(device.name) ? (
                    <img src={getDeviceImage(device.name)} alt={device.name} style={{width: '100%', height: '100%', objectFit: 'contain'}} />
                  ) : (
                    <span className="room-device-emoji">{device.icon}</span>
                  )}
                </div>
                <div className="room-device-info">
                  <h4 className="room-device-name">{device.name}</h4>
                  <p className="room-device-room">{device.room}</p>
                </div>
                <label className="toggle-switch" onClick={(e) => {
                  e.stopPropagation();
                  toggleDevice(device.id);
                }}>
                  <input
                    type="checkbox"
                    checked={isActive}
                    disabled={device.name === 'Smart Socket' && socketLoading}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleDevice(device.id);
                    }}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              );
            })}
          </div>
        </div>

        {/* Environmental Info Bar - Footer */}
        <div className="room-env-info">
          <div className="env-info-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0z" />
            </svg>
            <div className="env-info-text">
              <span className="env-label">Current temperature in the {roomName}</span>
              <span className="env-value">25°</span>
            </div>
          </div>
          <div className="env-info-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z" />
            </svg>
            <div className="env-info-text">
              <span className="env-label">Current humidity in the {roomName}</span>
              <span className="env-value">67%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomDetail;
